import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import './App.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { OrgMembershipProvider } from './context/OrgMembershipContext';
import AuthModal from './components/AuthModal';
import AuthCallback from './components/AuthCallback';
import FirstVisitModal from './components/FirstVisitModal';
import OnboardingModal from './components/OnboardingModal';
import { storeReferral, storeInvite } from './utils/referral';

// Backward-compat redirect: /donate-used-items/:id → /services/donate-used-items/:id
function RedirectDonateItem() {
  const { id } = useParams();
  return <Navigate to={`/services/donate-used-items/${id}`} replace />;
}

// Legacy org-invite redirect: /org-invite/:orgSlug/:token → /organizations/invite/:orgSlug?t=:token
function LegacyInviteRedirect() {
  const { orgSlug, token } = useParams();
  return <Navigate to={`/organizations/invite/${orgSlug}?t=${token}`} replace />;
}

// Catch-all for dead /changemaker-signup/* links
function ExpiredSignupPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 max-w-sm w-full p-10 text-center">
        <div className="text-4xl mb-4">🔗</div>
        <h1 className="text-lg font-bold text-gray-900 mb-2">This link has expired</h1>
        <p className="text-sm text-gray-500 mb-6">This invite link is no longer active. Please contact the organisation for a new link.</p>
        <a href="/" className="inline-block px-6 py-2.5 bg-green-600 text-white rounded-full text-sm font-medium hover:bg-green-700 transition-colors">
          Go to Karmana
        </a>
      </div>
    </div>
  );
}

// Eagerly loaded — landing page and primary navigation targets
import HomePage from './pages/HomePage';
import ActivitiesPage from './pages/ActivitiesPage';
import ActivityDetailPage from './pages/ActivityDetailPage';
import ChangemakersPage from './pages/ChangemakersPage';

// Lazy loaded — secondary pages, admin (Recharts ~500KB), auth-gated
const ChangemakerDetailPage = lazy(() => import('./pages/ChangemakerDetailPage'));
const OrgInvitePage          = lazy(() => import('./pages/OrgInvitePage'));
const OrgTeamInvitePage      = lazy(() => import('./pages/OrgTeamInvitePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CompanyDetailPage = lazy(() => import('./pages/CompanyDetailPage'));
const MyImpactPage = lazy(() => import('./pages/MyImpactPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DonateUsedItemPage = lazy(() => import('./pages/DonateUsedItemPage'));
const DonationPlacePage  = lazy(() => import('./pages/DonationPlacePage'));
const CutleryBankPage       = lazy(() => import('./pages/CutleryBankPage'));
const CutleryBankDetailPage = lazy(() => import('./pages/CutleryBankDetailPage'));
const InviteResolvePage     = lazy(() => import('./pages/InviteResolvePage'));
const CheckinPage           = lazy(() => import('./pages/CheckinPage'));
const MyJourneysPage        = lazy(() => import('./pages/MyJourneysPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

/* Renders the new-user 4-step onboarding modal globally */
function GlobalOnboarding() {
  const { onboardingOpen, closeOnboarding, user } = useAuth();
  if (!onboardingOpen) return null;
  return <OnboardingModal isOpen={onboardingOpen} onClose={closeOnboarding} user={user} />;
}

/** Redirect /changemakers/:slug → /organizations/:slug preserving history */
function LegacyChangemakerRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/organizations/${slug}`} replace />;
}

/** Redirect /organisations/:slug → /organizations/:slug (British → American spelling) */
function BritishOrgRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/organizations/${slug}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RoutedApp() {
  const location = useLocation();

  // Capture referral from share links: last-click attribution, 15-day TTL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref && params.get('utm_source') === 'app_share') {
      storeReferral(ref);
    }
    // Capture activity invite params: ?invited_by=userId&invite_activity=activityId
    const invitedBy = params.get('invited_by');
    const inviteActivity = params.get('invite_activity');
    if (invitedBy && inviteActivity) {
      storeInvite(invitedBy, inviteActivity);
    }
  }, [location.search]);

  // Process the Google OAuth callback BEFORE rendering normal routes.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin" /></div>}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/activities" element={<ActivitiesPage />} />
          <Route path="/activities/:id" element={<ActivityDetailPage />} />
          <Route path="/my-journeys" element={<MyJourneysPage />} />
          <Route path="/checkin/:activityId" element={<CheckinPage />} />
          <Route path="/i/:code" element={<InviteResolvePage />} />

          {/* Org invite — must use separate path segment for React Router v7 to rank higher than /:slug */}
          <Route path="/organizations/invite/:orgSlug" element={<OrgInvitePage />} />
          <Route path="/org-invite/:orgSlug/:token" element={<LegacyInviteRedirect />} />
          <Route path="/join/:token" element={<OrgTeamInvitePage />} />

          {/* New canonical organisation routes */}
          <Route path="/organizations" element={<ChangemakersPage />} />
          <Route path="/organizations/:slug" element={<ChangemakerDetailPage />} />

          {/* British-spelling aliases → redirect to canonical */}
          <Route path="/organisations" element={<Navigate to="/organizations" replace />} />
          <Route path="/organisations/:slug" element={<BritishOrgRedirect />} />

          {/* Legacy /changemakers/* — redirect to /organizations/* */}
          <Route path="/changemakers" element={<Navigate to="/organizations" replace />} />
          <Route path="/changemakers/:slug" element={<LegacyChangemakerRedirect />} />
          <Route path="/changemaker-signup/*" element={<ExpiredSignupPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:id" element={<CompanyDetailPage />} />
          <Route path="/my-impact" element={<MyImpactPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/donate-used-items"     element={<Navigate to="/services/donate-used-items" replace />} />
          <Route path="/donate-used-items/:id" element={<RedirectDonateItem />} />
          <Route path="/services/donate-used-items"     element={<DonateUsedItemPage />} />
          <Route path="/services/donate-used-items/:id" element={<DonationPlacePage />} />
          <Route path="/cutlery-bank" element={<CutleryBankPage />} />
          <Route path="/cutlery-bank/:id" element={<CutleryBankDetailPage />} />
        </Routes>
      </Suspense>
      <AuthModal />
      {/* <FirstVisitModal /> */}
      <GlobalOnboarding />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <OrgMembershipProvider>
            <div className="App">
              <RoutedApp />
            </div>
            <Toaster position="bottom-right" richColors />
            </OrgMembershipProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
